import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Compass, Target, Activity, BookOpen, ClipboardCheck, Trophy, LayoutDashboard, BarChart2 } from '../icons';
import { SidebarLayout } from './SidebarLayout';
import { RoleSwitcher } from '../RoleSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import type { SidebarItem } from './Sidebar';

export function RespondentLayout() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasPathways, setHasPathways] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      const { data: respondent } = await supabase
        .from('respondents')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!respondent) {
        navigate('/');
        return;
      }

      const { count } = await supabase
        .from('pathway_assignments')
        .select('*', { count: 'exact', head: true })
        .eq('respondent_user_id', user.id);

      setHasPathways((count || 0) > 0);
      setAuthorized(true);
      setChecking(false);
    };

    init();
  }, [user, authLoading, navigate]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Loading..." />;
  }

  const items: SidebarItem[] = [
    { group: 'Profile & Patterns', label: 'My Results', path: '/dashboard', icon: Brain },
  ];

  if (hasPathways) {
    items.push({ group: 'Profile & Patterns', label: 'My Pathways', path: '/dashboard/pathways', icon: Compass });
  }

  items.push(
    { group: 'Progress', label: 'Overview', path: '/dashboard/progress', icon: LayoutDashboard },
    { group: 'Progress', label: 'Goals', path: '/dashboard/progress/goals', icon: Target },
    { group: 'Progress', label: 'Habits', path: '/dashboard/progress/habits', icon: Activity },
    { group: 'Progress', label: 'Tasks', path: '/dashboard/progress/tasks', icon: ClipboardCheck },
    { group: 'Progress', label: 'Check-ins', path: '/dashboard/progress/check-ins', icon: Activity },
    { group: 'Progress', label: 'Journal', path: '/dashboard/progress/journal', icon: BookOpen },
    { group: 'Progress', label: 'Milestones', path: '/dashboard/progress/milestones', icon: Trophy },
    { group: 'Proof', label: 'My Reports', path: '/dashboard/proof/reports', icon: BarChart2 },
  );

  return (
    <SidebarLayout
      items={items}
      title="Profile & Patterns"
      subtitle="Assessment results & growth"
      headerSlot={<RoleSwitcher currentRole="respondent" />}
    />
  );
}
